import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, UserCog, CreditCard, PauseCircle, PlayCircle, Plus } from "lucide-react";
import { API, errMsg } from "../../lib/api";
import { useAuth, ADMIN_ROLES } from "../../context/AuthContext";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, StatusBadge, Money, Modal, Field, Table, Empty } from "../../components/common";

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && ADMIN_ROLES.includes(user.role);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [note, setNote] = useState("");
  const [noteVisible, setNoteVisible] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [credit, setCredit] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [resetResult, setResetResult] = useState(null);

  const load = () => API.get(`/admin/customers/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <Loading label="Loading customer" />;
  const b = data.business;

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const impersonate = async () => {
    try {
      const { data: res } = await API.post(`/admin/customers/${id}/impersonate-session`);
      sessionStorage.setItem("impersonation_token", res.access_token);
      window.location.href = "/portal";
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await act(() => API.post(`/admin/customers/${id}/notes`, { body: note, client_visible: noteVisible }), "Note added");
    setNote(""); setNoteVisible(false);
  };

  const TABS = [["overview", "Overview"], ["tax", "Tax Periods"], ["payments", "Payments"], ["documents", "Documents"],
    ["notes", "Notes & Comms"], ["billing", "Billing"], ["audit", "Audit Trail"]];

  return (
    <div data-testid="admin-customer-detail">
      <PageHeader title={b.legal_name} sub={<span className="font-mono text-xs">{b.case_number} · EIN {b.ein || "—"} · TPT {b.tpt_license || "—"}</span>}
        right={<>
          <StatusBadge status={b.status} className="text-sm" />
          {isAdmin && <>
            <button className="btn-outline-gov" onClick={() => { setPwOpen(true); setNewPw(""); setResetResult(null); }} data-testid="reset-pw-btn"><KeyRound className="h-4 w-4" /> Reset Password</button>
            <button className="btn-outline-gov" onClick={impersonate} data-testid="impersonate-btn"><UserCog className="h-4 w-4" /> Impersonate</button>
            {b.status === "suspended"
              ? <button className="btn-navy" onClick={() => act(() => API.patch(`/admin/customers/${id}`, { status: "active" }), "Customer reactivated")} data-testid="reactivate-btn"><PlayCircle className="h-4 w-4" /> Reactivate</button>
              : <button className="btn-copper" onClick={() => act(() => API.patch(`/admin/customers/${id}`, { status: "suspended" }), "Customer suspended")} data-testid="suspend-btn"><PauseCircle className="h-4 w-4" /> Suspend</button>}
            <button className="btn-outline-gov" onClick={() => setCreditOpen(true)} data-testid="credit-btn"><CreditCard className="h-4 w-4" /> Apply Credit</button>
            <button className="btn-outline-gov" onClick={() => setAssignOpen(true)} data-testid="assign-btn">Assign Staff</button>
          </>}
        </>} />

      <div className="mb-5 flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`cd-tab-${v}`}
            className={`rounded px-3 py-1.5 text-sm font-semibold ${tab === v ? "bg-navy text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="card-gov p-5">
            <h3 className="mb-3 font-bold text-navy">Business</h3>
            <dl className="space-y-2 text-sm">
              {[["DBA", b.dba], ["Entity", b.entity_type], ["Industry", b.industry], ["Phone", b.phone],
                ["Email", b.email], ["Filing frequency", b.filing_frequency || "Not verified"],
                ["Accounting", `${b.accounting_basis} / ${b.fiscal_year}`], ["Since", fmtDate(b.created_at)]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3"><dt className="text-slate-400">{k}</dt><dd className="font-medium text-slate-700 text-right">{v || "—"}</dd></div>
              ))}
            </dl>
          </div>
          <div className="card-gov p-5">
            <h3 className="mb-3 font-bold text-navy">Account Users</h3>
            {data.users.map((u) => (
              <div key={u.id} className="mb-2 rounded border border-slate-200 px-3 py-2 text-sm">
                <div className="font-semibold text-slate-800">{u.name}</div>
                <div className="text-xs text-slate-400">{u.email} · {u.role.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <div className="card-gov p-5">
            <h3 className="mb-3 font-bold text-navy">Financial Position</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-400">Transactions</dt><dd className="money font-semibold">{data.transaction_count}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Vault balance</dt><dd className="money font-semibold"><Money cents={data.vault_balance_cents} /></dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Plan</dt><dd className="font-semibold">{data.plan?.name || "None"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Subscription</dt><dd><StatusBadge status={b.subscription_status || "none"} /></dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Account credit</dt><dd className="money font-semibold text-emerald-700"><Money cents={b.credit_cents || 0} exact /></dd></div>
            </dl>
            <h4 className="mt-4 mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Onboarding</h4>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {Object.entries(b.onboarding || {}).map(([k, v]) => (
                <span key={k} className={v ? "text-emerald-700" : "text-amber-700"}>{v ? "✓" : "○"} {k.replace(/_/g, " ")}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "tax" && (
        data.periods.length === 0 ? <Empty title="No tax periods" /> : (
          <Table headers={["Period", "Gross", "Taxable", "Liability", "Balance", "Due", "Status"]} testid="cd-periods">
            {data.periods.map((p) => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="px-4 py-2.5 font-semibold text-navy">{p.label}</td>
                <td className="px-4 py-2.5 money"><Money cents={p.gross_cents} /></td>
                <td className="px-4 py-2.5 money"><Money cents={p.taxable_cents} /></td>
                <td className="px-4 py-2.5 money font-semibold"><Money cents={p.liability_cents} /></td>
                <td className="px-4 py-2.5 money"><Money cents={p.balance_cents} /></td>
                <td className="px-4 py-2.5">{fmtDate(p.due_date)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
              </tr>
            ))}
          </Table>
        )
      )}

      {tab === "payments" && (
        data.payments.length === 0 ? <Empty title="No tax payments" /> : (
          <Table headers={["Case #", "Type", "Amount", "Due", "Status", "Confirmation"]} testid="cd-payments">
            {data.payments.map((p) => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="px-4 py-2.5 font-mono text-xs">{p.case_number}</td>
                <td className="px-4 py-2.5">{p.tax_type}</td>
                <td className="px-4 py-2.5 money font-semibold"><Money cents={p.amount_cents} exact /></td>
                <td className="px-4 py-2.5">{fmtDate(p.due_date)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-2.5 font-mono text-xs">{p.confirmation_number || "—"}</td>
              </tr>
            ))}
          </Table>
        )
      )}

      {tab === "documents" && (
        data.documents.length === 0 ? <Empty title="No documents" /> : (
          <Table headers={["File", "Folder", "Size", "Uploaded"]} testid="cd-documents">
            {data.documents.map((d) => (
              <tr key={d.id} className="border-b border-slate-100">
                <td className="px-4 py-2.5 font-medium">{d.filename}</td>
                <td className="px-4 py-2.5">{d.folder}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{(d.size / 1024).toFixed(1)} KB</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{fmtDate(d.created_at)} by {d.uploaded_by}</td>
              </tr>
            ))}
          </Table>
        )
      )}

      {tab === "notes" && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="card-gov p-5">
            <h3 className="mb-3 font-bold text-navy">Add Note</h3>
            <textarea className="input-gov" rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" placeholder="Internal note or client-visible message…" />
            <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={noteVisible} onChange={(e) => setNoteVisible(e.target.checked)} data-testid="note-visible" /> Visible to client (internal notes are never shown unless checked)
            </label>
            <button className="btn-navy mt-3" onClick={addNote} data-testid="note-add"><Plus className="h-4 w-4" /> Add Note</button>
          </div>
          <div className="space-y-2">
            {data.notes.map((n) => (
              <div key={n.id} className={`rounded-md border p-3 text-sm ${n.client_visible ? "border-sky-200 bg-sky-50" : "border-slate-200 bg-white"}`}>
                <div className="flex justify-between text-xs text-slate-400">
                  <span>{n.author}</span>
                  <span>{n.client_visible ? "CLIENT VISIBLE" : "INTERNAL"} · {fmtDate(n.created_at)}</span>
                </div>
                <div className="mt-1 text-slate-700">{n.body}</div>
              </div>
            ))}
            {data.notes.length === 0 && <Empty title="No notes yet" />}
          </div>
        </div>
      )}

      {tab === "billing" && (
        data.billing.length === 0 ? <Empty title="No billing activity" msg="Stripe subscription checkouts appear here." /> : (
          <Table headers={["Date", "Lookup", "Amount", "Status"]} testid="cd-billing">
            {data.billing.map((t) => (
              <tr key={t.id} className="border-b border-slate-100">
                <td className="px-4 py-2.5 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{t.lookup_key}</td>
                <td className="px-4 py-2.5 money"><Money cents={t.amount_cents} exact /></td>
                <td className="px-4 py-2.5"><StatusBadge status={t.payment_status === "paid" ? "PAID" : t.status} /></td>
              </tr>
            ))}
          </Table>
        )
      )}

      {tab === "audit" && (
        data.audit_logs.length === 0 ? <Empty title="No audit entries" /> : (
          <Table headers={["Time", "User", "Action", "Record", "IP"]} testid="cd-audit">
            {data.audit_logs.map((a) => (
              <tr key={a.id} className="border-b border-slate-100">
                <td className="px-4 py-2.5 text-xs text-slate-500">{fmtDate(a.created_at)}</td>
                <td className="px-4 py-2.5 text-sm">{a.user_email}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{a.action}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{a.record_type || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{a.ip || "—"}</td>
              </tr>
            ))}
          </Table>
        )
      )}

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)} title="Assign Staff">
        <div className="space-y-4" data-testid="assign-modal">
          {[["assigned_tax_pro", "Tax Professional"], ["assigned_account_manager", "Account Manager"]].map(([field, label]) => (
            <Field key={field} label={label}>
              <select className="input-gov" defaultValue={b[field] || ""} onChange={(e) => act(() => API.patch(`/admin/customers/${id}`, { [field]: e.target.value || null }), `${label} assigned`)} data-testid={`assign-${field}`}>
                <option value="">Unassigned</option>
                {data.staff.filter((s) => field === "assigned_tax_pro" ? ["TAX_PROFESSIONAL", "ADMIN", "SUPER_ADMIN"].includes(s.role) : true).map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.role.replace(/_/g, " ")})</option>
                ))}
              </select>
            </Field>
          ))}
        </div>
      </Modal>

      <Modal open={pwOpen} onClose={() => setPwOpen(false)} title="Reset Customer Password">
        {resetResult ? (
          <div className="space-y-4" data-testid="reset-pw-result">
            <p className="text-sm text-slate-600">Password updated for <span className="font-semibold">{resetResult.email}</span>. Any login lockout has been cleared. Share this password securely:</p>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-center font-mono text-lg font-bold tracking-wide" data-testid="reset-pw-value">{resetResult.temporary_password}</div>
            <button className="btn-navy w-full" onClick={() => setPwOpen(false)} data-testid="reset-pw-done">Done</button>
          </div>
        ) : (
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              const r = await API.post(`/admin/customers/${id}/reset-password`, newPw ? { password: newPw } : {});
              setResetResult(r.data); toast.success("Password reset; lockout cleared");
            } catch (err) { toast.error(errMsg(err)); }
          }} className="space-y-4" data-testid="reset-pw-form">
            <Field label="New Password (leave blank to auto-generate)">
              <input className="input-gov" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={8} placeholder="Minimum 8 characters" data-testid="reset-pw-input" />
            </Field>
            <p className="text-xs text-slate-500">This also clears any temporary login lockout and invalidates outstanding reset email links.</p>
            <button className="btn-navy w-full" data-testid="reset-pw-submit">Reset Password</button>
          </form>
        )}
      </Modal>

      <Modal open={creditOpen} onClose={() => setCreditOpen(false)} title="Apply Account Credit">
        <form onSubmit={(e) => { e.preventDefault(); act(() => API.patch(`/admin/customers/${id}`, { apply_credit: credit }), "Credit applied"); setCreditOpen(false); }} className="space-y-4" data-testid="credit-form">
          <Field label="Credit Amount ($)"><input className="input-gov money" inputMode="decimal" value={credit} onChange={(e) => setCredit(e.target.value)} required data-testid="credit-amount" /></Field>
          <button className="btn-navy w-full" data-testid="credit-submit">Apply Credit</button>
        </form>
      </Modal>
    </div>
  );
}
